/**
 * useEmailSearch Hook
 *
 * Search with advanced filters and natural language search via React Query.
 */

import { useQuery } from '@tanstack/react-query';
import { api, EmailMessage, EmailSearchOptions } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

interface UseEmailSearchOptions extends Omit<EmailSearchOptions, 'accountId'> {
  accountId: string | undefined;
  enabled?: boolean;
}

export function useEmailSearch(options: UseEmailSearchOptions) {
  const { accountId, enabled = true, ...filters } = options;

  const hasFilters = !!(
    filters.q ||
    filters.from ||
    filters.to ||
    filters.subject ||
    filters.hasAttachment !== undefined ||
    filters.isRead !== undefined ||
    filters.isStarred !== undefined ||
    filters.folder ||
    filters.label ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.importance
  );

  const queryResult = useQuery({
    queryKey: queryKeys.email.search({ accountId, ...filters }),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.searchEmailMessages({ accountId, ...filters });
    },
    enabled: !!accountId && enabled && hasFilters,
  });

  return {
    messages: queryResult.data?.messages ?? ([] as EmailMessage[]),
    total: queryResult.data?.total ?? 0,
    limit: queryResult.data?.limit ?? 50,
    offset: queryResult.data?.offset ?? 0,
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
  };
}
